import { AuthDenialCode } from '@gearbelt/global';

import { userManager } from '@/lib/fusionauth';
import { useAuthDenialStore } from '@/stores/auth-denial.store';
import { useUserStore } from '@/stores/user.store';

/**
 * Only a 401 on a request that carried a token proves the session is gone. A
 * coded 403 surfaces as a modal; an uncoded one belongs to the calling feature.
 */
export async function handleAuthResponse(request: Request, response: Response): Promise<void> {
  if (response.status === 401) {
    // A request that went out without a token says nothing about the session.
    if (!request.headers.get('Authorization')) {
      return;
    }

    const userStore = useUserStore.getState();
    userStore.setOrg(null);
    userStore.setUser(null);

    await userManager.signoutRedirect({
      post_logout_redirect_uri: window.location.origin,
    });
    return;
  }

  if (response.status !== 403) {
    return;
  }

  // Clone first: reading the body here consumes the stream, and every existing
  // 403 handler in the app would receive an empty one.
  const body = await response
    .clone()
    .json()
    .then((parsed: { code?: unknown }) => parsed)
    .catch(() => null);

  // Checked at runtime, not just typed: a server that ships a new code ahead of
  // this client would otherwise reach the modal's lookup table with no entry.
  if (!isKnownDenialCode(body?.code)) {
    return;
  }

  useAuthDenialStore.getState().setDeniedReason(body.code);
}

function isKnownDenialCode(code: unknown): code is AuthDenialCode {
  return typeof code === 'string' && (Object.values(AuthDenialCode) as string[]).includes(code);
}
